import { httpClient } from '@manager/api/http-client';
import type { IResponse } from '@manager/api/types/general-types';
import type { IRefreshTranslationsPayload } from '@manager/api/types/translation-types';
import { getApiBasePath } from '@manager/utils/request-utils';
import { isString, setWith } from 'lodash-es';

const toEscape = '@'.split('');

function getSanitizedValue(stringValue: string): string {
  return stringValue
    .split('')
    .map((char) => {
      if (toEscape.includes(char)) {
        return `{'${char}'}`;
      }

      return char;
    })
    .join('');
}

function isBelowOtherMessage(
  key: string,
  messages: Record<string, unknown>
): boolean {
  const segments = key.split('.');

  for (let length = 1; length < segments.length; length++) {
    if (Object.hasOwn(messages, segments.slice(0, length).join('.'))) {
      return true;
    }
  }

  return false;
}

/**
 * The API sends flat keys, but `te()` only finds nested messages. A key below another message
 * (`validation.min.numeric`) cannot nest, so it stays flat for the flat-key fallback of `t()`.
 */
function toNestedMessages(
  messages: Record<string, unknown>
): Record<string, unknown> {
  const nested = {};

  Object.entries(messages).forEach(([key, value]) => {
    const message = isString(value) ? getSanitizedValue(value) : value;
    const path = isBelowOtherMessage(key, messages) ? [key] : key.split('.');
    // `Object` keeps numeric segments (`renewal_days.1`) as object keys, not array indexes.
    setWith(nested, path, message, Object);
  });

  return nested;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getTranslations(): Promise<IResponse<Record<string, any>>> {
  return httpClient.get(`${getApiBasePath()}/translation`).then((res) => {
    res.data = toNestedMessages(res.data);
    return res;
  });
}

export function refreshTranslations(
  payload?: IRefreshTranslationsPayload
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IResponse<Record<string, any>>> {
  return httpClient.put(`${getApiBasePath()}/translation/refresh`, payload);
}
